import { useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Mail, ExternalLink, Download, Instagram, Music, StickyNote, Linkedin, Table2, Columns3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { usePublish, PublishTarget, PublishButton } from "./PublishToClient";
import { adminApi } from "@/lib/api";
import { KanbanBoard } from "./KanbanBoard";
import { CRM_STATUSES, crmMeta, CrmStatusSelect, CrmModal, ReviewsCell, EmailVerifBadge } from "./CrmControls";

const selectCls = "h-10 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] rounded-sm text-sm px-3";

const VISIBLE = "#16a34a";

const LeadsMap = ({ leads, visibleIds = [] }) => {
  const pts = leads.filter((l) => l.lat && l.lon);
  const center = pts.length ? [pts[0].lat, pts[0].lon] : [45.9, 10.0];
  return (
    <div data-testid="leadgen-map" className="border border-[#d5dbde] bg-white">
      <div className="px-5 py-3 border-b border-[#d5dbde] flex flex-wrap items-center gap-4">
        <h3 className="font-display text-xl mr-auto">Mappa lead</h3>
        {CRM_STATUSES.map((s) => (
          <span key={s.id} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-[#5b6168]">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />{s.label}
          </span>
        ))}
        <span data-testid="map-legend-visible" className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-[#2B3036]">
          <span className="w-3 h-3 rounded-full ring-2 ring-[#2B3036]" style={{ background: VISIBLE }} />Visibile al cliente ({pts.filter((l) => visibleIds.includes(l.id)).length})
        </span>
        <span className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280]">{pts.length} sulla mappa</span>
      </div>
      <div style={{ height: 380 }}>
        <MapContainer key={pts.length ? `${pts[0].id}` : "empty"} center={center} zoom={pts.length ? 7 : 5}
          style={{ height: "100%", width: "100%", background: "#f4f6f7" }}>
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          {[...pts].sort((a, b) => visibleIds.includes(a.id) - visibleIds.includes(b.id)).map((l) => {
            const c = crmMeta(l.crm_status).color;
            const vis = visibleIds.includes(l.id);
            return (
              <CircleMarker key={`${l.id}-${vis}`} center={[l.lat, l.lon]} radius={vis ? 10 : 7}
                pathOptions={vis ? { color: "#2B3036", fillColor: VISIBLE, fillOpacity: 0.95, weight: 2.5 } : { color: c, fillColor: c, fillOpacity: 0.75, weight: 1.5 }}>
                <Popup>
                  <div style={{ fontFamily: "sans-serif", fontSize: 12 }}>
                    <strong>{l.company_name}</strong><br />{l.city} ({l.country})<br />
                    Stato: <b style={{ color: c }}>{crmMeta(l.crm_status).label}</b>
                    {l.crm_note ? <><br /><i>{l.crm_note}</i></> : null}
                    {vis ? <><br /><b style={{ color: VISIBLE }}>✓ Visibile al cliente</b></> : null}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

const SocialLinks = ({ l }) => (
  <div className="flex gap-1.5">
    {l.instagram_url && (
      <a href={l.instagram_url} target="_blank" rel="noreferrer" title="Instagram"
        className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-[#E64FA7] to-[#FF8A3D] rounded-sm">
        <Instagram className="w-3.5 h-3.5 text-white" />
      </a>
    )}
    {l.tiktok_url && (
      <a href={l.tiktok_url} target="_blank" rel="noreferrer" title="TikTok"
        className="w-7 h-7 flex items-center justify-center bg-black border border-[#d5dbde] rounded-sm">
        <Music className="w-3.5 h-3.5 text-white" />
      </a>
    )}
    {l.linkedin_url && (
      <a href={l.linkedin_url} target="_blank" rel="noreferrer" title="LinkedIn"
        className="w-7 h-7 flex items-center justify-center bg-[#0a66c2] rounded-sm">
        <Linkedin className="w-3.5 h-3.5 text-white" />
      </a>
    )}
  </div>
);

const EmailStatus = ({ s }) => {
  if (s === "sent") return <span className="text-[10px] font-mono uppercase bg-[#10b981] text-white px-2 py-0.5">inviata</span>;
  if (s === "error") return <span className="text-[10px] font-mono uppercase bg-[#ef4444] text-white px-2 py-0.5">errore</span>;
  return <span className="text-[10px] font-mono uppercase bg-[#e4e7e9] text-[#5b6168] px-2 py-0.5">da inviare</span>;
};

const LeadRow = ({ l, selected, onToggle, onEmail, onCrm, onUpdated, pub }) => (
  <tr data-testid={`lead-row-${l.id}`} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
    <td className="px-3 py-3">
      <Checkbox data-testid={`select-lead-${l.id}`} checked={selected} onCheckedChange={onToggle} disabled={!l.email} />
    </td>
    <td className="px-3 py-3 min-w-[160px]">
      <div className="font-medium text-[#2B3036] truncate max-w-[180px]">{l.company_name}</div>
      <div className="text-[10px] font-mono text-[#6b7280]">{l.source === "google_places" ? "Google Places" : l.ateco_code || "—"}</div>
    </td>
    <td className="px-3 py-3 font-mono text-xs">{l.vat || "—"}</td>
    <td className="px-3 py-3 text-xs">{l.contact_first_name} {l.contact_last_name}
      {l.contact_position && <div className="text-[10px] text-[#6b7280]">{l.contact_position}</div>}
    </td>
    <td className="px-3 py-3"><div>{l.city}</div><div className="text-[10px] font-mono text-[#6b7280]">{l.country}</div></td>
    <td className="px-3 py-3 text-xs">
      <div className="font-mono">{l.phone}</div>
      <div className="font-mono text-[#6b7280] truncate max-w-[160px]">{l.email || "—"}</div>
      <EmailVerifBadge status={l.email_verification} />
    </td>
    <td className="px-3 py-3 text-xs"><ReviewsCell lead={l} /></td>
    <td className="px-3 py-3"><SocialLinks l={l} /></td>
    <td className="px-3 py-3"><EmailStatus s={l.email_status} /></td>
    <td className="px-3 py-3"><CrmStatusSelect lead={l} onUpdated={onUpdated} /></td>
    <td className="px-3 py-3"><PublishButton p={pub} lead={l} /></td>
    <td className="px-3 py-3">
      <div className="flex gap-1">
        {l.website && (
          <a href={l.website} target="_blank" rel="noreferrer" title="Vedi sito"
            className="w-7 h-7 flex items-center justify-center border border-[#d5dbde] hover:border-[#E0262C]">
            <ExternalLink className="w-3.5 h-3.5 text-[#5b6168]" />
          </a>
        )}
        <button data-testid={`crm-note-${l.id}`} onClick={onCrm} title={l.crm_note || "Nota CRM"}
          className={`w-7 h-7 flex items-center justify-center border hover:border-[#E0262C] ${l.crm_note ? "border-[#f59e0b]" : "border-[#d5dbde]"}`}>
          <StickyNote className={`w-3.5 h-3.5 ${l.crm_note ? "text-[#f59e0b]" : "text-[#5b6168]"}`} />
        </button>
        <button data-testid={`send-email-${l.id}`} onClick={onEmail} disabled={!l.email} title="Invia email"
          className="w-7 h-7 flex items-center justify-center aurora-gradient hover:opacity-90 disabled:opacity-30">
          <Mail className="w-3.5 h-3.5 text-[#2B3036]" />
        </button>
      </div>
    </td>
  </tr>
);

export const ResultsPanel = ({ leads, onEmail, onUpdated, target }) => {
  const [filterCity, setFilterCity] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCrm, setFilterCrm] = useState("");
  const [selected, setSelected] = useState([]);
  const [crmLead, setCrmLead] = useState(null);
  const [view, setView] = useState("table");
  const pub = usePublish(target);

  const filtered = useMemo(() => leads.filter((l) =>
    (!filterCity || (l.city || "").toLowerCase().includes(filterCity.toLowerCase())) &&
    (!filterStatus || l.email_status === filterStatus) &&
    (!filterCrm || (l.crm_status || "nuovo") === filterCrm)
  ), [leads, filterCity, filterStatus, filterCrm]);

  const counts = useMemo(() => Object.fromEntries(CRM_STATUSES.map((s) =>
    [s.id, leads.filter((l) => (l.crm_status || "nuovo") === s.id).length])), [leads]);

  const selectable = filtered.filter((l) => l.email);
  const allSelected = selectable.length > 0 && selectable.every((l) => selected.includes(l.id));
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelected(allSelected ? [] : selectable.map((l) => l.id));
  const sendBulk = () => onEmail(leads.filter((l) => selected.includes(l.id)));

  return (
    <div className="space-y-6">
      <div data-testid="crm-pipeline" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#e4e7e9] border border-[#d5dbde]">
        {CRM_STATUSES.map((s) => (
          <button key={s.id} data-testid={`crm-pipeline-${s.id}`} onClick={() => setFilterCrm(filterCrm === s.id ? "" : s.id)}
            className={`bg-white px-5 py-4 text-left transition-colors hover:bg-[#f4f6f7] ${filterCrm === s.id ? "bg-[#f4f6f7]" : ""}`}
            style={filterCrm === s.id ? { boxShadow: `inset 0 -2px 0 ${s.color}` } : undefined}>
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: s.color }}>{s.label}</div>
            <div className="font-display text-3xl text-[#2B3036] mt-1">{counts[s.id]}</div>
          </button>
        ))}
      </div>

      <LeadsMap leads={filtered} visibleIds={pub.info.lead_ids} />

      <PublishTarget p={pub} />

      <div className="flex flex-wrap items-center gap-3">
        <div data-testid="view-toggle" className="flex border border-[#d5dbde]">
          {[["table", "Tabella", Table2], ["kanban", "Kanban", Columns3]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`view-toggle-${id}`} onClick={() => setView(id)}
              className={`px-3 h-10 text-sm inline-flex items-center gap-1.5 transition-colors ${view === id ? "bg-[#f4f6f7] text-[#2B3036]" : "text-[#6b7280] hover:text-[#E0262C]"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <Input data-testid="filter-city" placeholder="Filtra città" value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
          className="h-10 w-48 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
        <select data-testid="filter-email-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectCls}>
          <option value="">Tutte le email</option>
          <option value="pending">Da inviare</option>
          <option value="sent">Inviata</option>
          <option value="error">Errore</option>
        </select>
        <select data-testid="filter-crm-status" value={filterCrm} onChange={(e) => setFilterCrm(e.target.value)} className={selectCls}>
          <option value="">Tutti gli stati CRM</option>
          {CRM_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <button data-testid="bulk-email-btn" onClick={sendBulk} disabled={!selected.length}
          className="inline-flex items-center gap-2 aurora-gradient px-4 py-2 text-sm text-white disabled:opacity-30">
          <Mail className="w-3.5 h-3.5" /> Email ai selezionati ({selected.length})
        </button>
        <span data-testid="leads-count" className="text-xs font-mono text-[#6b7280] ml-auto">{filtered.length} / {leads.length} lead</span>
        <a href={adminApi.exportCsv()} className="inline-flex items-center gap-2 border border-[#d5dbde] hover:border-[#E0262C] px-3 py-2 text-sm text-[#2B3036]">
          <Download className="w-3.5 h-3.5" /> CSV
        </a>
        <a href={adminApi.exportXlsx()} className="inline-flex items-center gap-2 bg-[#10b981] hover:bg-[#059669] px-3 py-2 text-sm text-white">
          <Download className="w-3.5 h-3.5" /> XLSX
        </a>
      </div>

      {view === "kanban" ? (
        <KanbanBoard leads={filtered} onUpdated={onUpdated} onEmail={onEmail} onCrm={setCrmLead} />
      ) : (
      <div data-testid="leadgen-results-table" className="border border-[#d5dbde] overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              <th className="px-3 py-3"><Checkbox data-testid="select-all-leads" checked={allSelected} onCheckedChange={toggleAll} /></th>
              {["Azienda", "P.IVA / VAT", "Referente", "Città", "Contatti", "Reviews", "Social", "Email", "CRM", "Cliente", "Azioni"].map((h) => (
                <th key={h} className="px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <LeadRow key={l.id} l={l} selected={selected.includes(l.id)} onToggle={() => toggle(l.id)}
                onEmail={() => onEmail([l])} onCrm={() => setCrmLead(l)} onUpdated={onUpdated} pub={pub} />
            ))}
            {!filtered.length && (
              <tr><td colSpan={12} className="px-4 py-16 text-center text-[#6b7280] font-mono text-xs">Nessun lead corrisponde ai filtri</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {crmLead && <CrmModal lead={crmLead} onClose={() => setCrmLead(null)} onUpdated={onUpdated} />}
    </div>
  );
};
